import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination
} from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import UserMoreMenu from '../../components/_dashboard/user/UserMoreMenu';
import UserListHead from '../../components/_dashboard/user/UserListHead';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import partnerService from '../services/partnerService';
import { fDate } from '../../utils/formatTime';
// styling
import { StyledTableRow, StyledTableCell } from '../Global/Styling';
// import OnBoardingToolbar from './OnBoardingToolbar';
import PartnersListToolbar from './PartnersListToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getOnboardingsList } from '../../store/Slice/Onboarding/OnboardingSlice';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------
const { UpdatePartnerStatus, PartnerStatusTypes } = partnerService;
const TABLE_HEAD = [
  { id: 'id', label: 'Partner ID', alignRight: false },
  { id: 'partnerName', label: 'Partner Name', alignRight: false },
  { id: 'partnerType', label: 'Partnery Type', alignRight: false },
  { id: 'city', label: 'Location', alignRight: false },
  { id: 'randomPhoneNumber', label: 'Mobile', alignRight: false },
  { id: 'created', label: 'Date Of Birth', alignRight: false },
  { id: 'active', label: 'Status', alignRight: false },
  { id: '', label: 'ACTION' }
];

export default function OnBoarding() {
  const pageCode = 'PARTNER_PAGE';
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageMenu, setPageMenu] = useState([]); // State for store localstorage data so that we can provide data to others components
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { OnboardingListData } = useOnboardingSelector();
  const { AccessLevelConfig } = useGlobalSelector(); // sidebar data
  const debouncedSearch = useDebounce(filterName, 600);

  // loan type & Bank Dropdown
  const getOnboardingListAPi = useCallback(() => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : '',
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        sortOrder: order || 'desc'
      },
      filterModel: {
        typeId: null,
        partnerStatusTypeId: null,
        cityId: null
      }
    };
    dispatch(getOnboardingsList(data));
  }, [LoginData, debouncedSearch, page, rowsPerPage, order, dispatch]);

  useEffect(() => {
    getOnboardingListAPi();
  }, [getOnboardingListAPi]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = OnboardingListData?.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClick = (event, partner) => {
    const selectedIndex = selected.indexOf(partner);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, partner);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (OnboardingListData?.length ?? 0)) : 0;

  const UpdatePartnerStatusApi = async (rowId, partner) => {
    const PartnerStatusTypesApi = await PartnerStatusTypes(LoginData?.jwt);
    if (PartnerStatusTypesApi.status === 'success') {
      let partnerStatusTypeId = '';
      PartnerStatusTypesApi.data.partnerStatusTypes.forEach((item, index) => {
        if (item.typeName === 'Activated') {
          partnerStatusTypeId = item.typeId;
        }
      });
      const statusCredential = {
        companyId: partner.companyId,
        partnerId: rowId,
        partnerStatusTypeId: partnerStatusTypeId
      };
      const UpdatePartnerStatusResponse = await UpdatePartnerStatus(
        statusCredential,
        LoginData?.jwt
      );
      if (UpdatePartnerStatusResponse.status === 'success') {
        getOnboardingListAPi();
      }
    }
  };

  useEffect(() => {
    if (AccessLevelConfig) {
      AccessLevelConfig?.accessPagesMapping?.forEach((accessPageMap) => {
        if (accessPageMap?.pageCode === pageCode) {
          setPageMenu(accessPageMap?.accessSubPagesMappings);
        }
      });
    }
  }, [AccessLevelConfig]);

  useEffect(() => {
    if (!OnboardingListData) {
      setHasNextPage(true);
      return;
    }

    // If API returned fewer rows → last page
    setHasNextPage(OnboardingListData.length >= rowsPerPage);
  }, [OnboardingListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [filterName, rowsPerPage]);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);
  return (
    <Page title="Kpros by Kalolytic Solutions || On Boarding">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="List of Partners" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Card>
            <PartnersListToolbar
              numSelected={selected.length}
              filterName={filterName}
              onFilterName={handleFilterByName}
              parent="partners"
              pageMenu={pageMenu}
            />
            {/* {console.log('sss OnboardingListData', OnboardingListData)} */}
            <Scrollbar>
              {OnboardingListData && OnboardingListData?.length > 0 && (
                <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={OnboardingListData?.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                      isCheckBox="true"
                    />
                    <TableBody>
                      {Array.isArray(OnboardingListData) &&
                        OnboardingListData?.map((partner, index) => {
                          const isItemSelected = selected.indexOf(partner) !== -1;
                          return (
                            <StyledTableRow
                              hover
                              key={index}
                              tabIndex={-1}
                              role="checkbox"
                              selected={isItemSelected}
                              aria-checked={isItemSelected}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isItemSelected}
                                  onChange={(event) => handleClick(event, partner)}
                                />
                              </TableCell>

                              <StyledTableCell align="left">
                                {partner?.partnerState === null ||
                                partner?.partnerStatus?.typeName === 'Rejected' ||
                                partner?.partnerStatus?.typeName === 'Approved' ||
                                partner?.partnerStatus?.typeName === 'Inactive'
                                  ? ''
                                  : partner?.partnerIdToDisplay}
                              </StyledTableCell>
                              <StyledTableCell align="left">{partner?.partnerName}</StyledTableCell>
                              <StyledTableCell align="left">
                                {partner?.partnerType?.typeName}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {partner?.cityEntity !== null
                                  ? partner?.cityEntity?.cityName
                                  : 'N/A'}
                              </StyledTableCell>
                              <StyledTableCell align="left">{partner?.mobile}</StyledTableCell>
                              <StyledTableCell align="left">{fDate(partner?.dob)}</StyledTableCell>
                              <StyledTableCell align="left">
                                {partner.partnerStatus !== null ? (
                                  <Label
                                    variant="ghost"
                                    color={
                                      (partner?.partnerStatus?.typeName === 'Rejected' &&
                                        'error') ||
                                      (partner?.partnerStatus?.typeName === 'Approved' &&
                                        'warning') ||
                                      (partner?.partnerStatus?.typeName === 'Activated' &&
                                        'success') ||
                                      (partner?.partnerStatus?.typeName === 'Inactive' &&
                                        'greymsg') ||
                                      'warning'
                                    }
                                  >
                                    {partner?.partnerStatus?.typeName}
                                  </Label>
                                ) : (
                                  'No status'
                                )}
                              </StyledTableCell>

                              <StyledTableCell align="">
                                {/* <StyledTableCell align="" width={115}> */}
                                <UserMoreMenu
                                  partner={partner}
                                  rowId={partner?.partnerId}
                                  parent="partner"
                                  pageMenu={pageMenu}
                                />
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 53 * emptyRows }}>
                          <StyledTableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                    {OnboardingListData?.length === 0 && (
                      <TableBody>
                        <TableRow>
                          <StyledTableCell
                            align="center"
                            colSpan={TABLE_HEAD.length + 1}
                            sx={{ py: 3 }}
                          >
                            <SearchNotFound searchQuery={filterName} />
                          </StyledTableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              )}
            </Scrollbar>
            <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
            {/* <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={OnboardingListData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            /> */}
          </Card>
        </Box>
      </Container>
    </Page>
  );
}
